import { useAuthFetch, useGuardedEffect } from "@p0security/frontend-core";
import yaml from "js-yaml";
import { useCallback } from "react";
import { usePolicyStore } from "store/policyStore";

import { DEFAULT_DOCUMENT, DEFAULT_RULE } from "./utils";

/** Loads policy/latest into the policies store.
 *  Skips the fetch if the store already has data (unless `force` is true).
 *  The `force` parameter is used for the EditPage.
 */
export const useLoadPolicies = ({
  force = false,
}: { force?: boolean } = {}) => {
  const {
    currentVersion,
    jsonRules,
    setCurrentVersion,
    setErrors,
    setIsLoading,
    setJsonRules,
    setYamlRules,
  } = usePolicyStore();

  const setSingleError = useCallback(
    (error: string | undefined) => setErrors(error ? [error] : []),
    [setErrors]
  );

  const authFetch = useAuthFetch(setSingleError);

  const alreadyLoaded = currentVersion !== undefined || jsonRules.length > 0;

  useGuardedEffect(
    (cancellation) => async () => {
      if (!force && alreadyLoaded) return;
      try {
        setIsLoading(true);
        const response = await authFetch("policy/latest", {
          method: "GET",
          onNotOk: (response) => {
            if (response.status === 404) {
              if (cancellation.isCancelled) return;
              setCurrentVersion(undefined);
              setJsonRules([DEFAULT_RULE]);
              setYamlRules(DEFAULT_DOCUMENT);
              setIsLoading(false);
              return;
            }
          },
        });
        if (!response) return;
        const {
          policy: { rules, version },
        } = await response.json();
        if (cancellation.isCancelled) return;
        setCurrentVersion(version);
        setJsonRules(rules);
        setYamlRules(yaml.dump(rules, { flowLevel: 4 }));
      } finally {
        cancellation.guard(setIsLoading)(false);
      }
    },
    [
      alreadyLoaded,
      authFetch,
      force,
      setCurrentVersion,
      setIsLoading,
      setJsonRules,
      setYamlRules,
    ],
    setSingleError
  );
};
